// eslint-disable-next-line import/extensions
import scorpContent from './content/scorp.json';
// eslint-disable-next-line import/extensions
import llcContent from './content/llc.json';
import { EntityType, Usertype } from './types';
import { shiftRowsToNow } from './seedDates';

// Journeys per end-state entity — the Journey dropdown only offers those that lead to the
// token's entity_type (an LLC demo never shows an S-Corp journey).
export const JOURNEYS_BY_ENTITY: Record<EntityType, Usertype[]> = {
    scorp: ['SP-SC', 'LLC-SC', 'SC-SC'],
    llc: ['SP-LLC', 'LLC-LLC'],
};

export const JOURNEY_LABEL: Record<Usertype, string> = {
    'SP-SC': 'Sole prop → S Corp',
    'LLC-SC': 'LLC → S Corp',
    'SC-SC': 'Existing S Corp',
    'SP-LLC': 'Sole prop → LLC',
    'LLC-LLC': 'Existing LLC',
};

export function defaultJourneyFor(entityType: EntityType): Usertype {
    return JOURNEYS_BY_ENTITY[entityType][0];
}

// Keep a journey consistent with entity_type; fall back to the default otherwise.
export function normalizeJourney(entityType: EntityType, journey?: string | null): Usertype {
    const valid = JOURNEYS_BY_ENTITY[entityType];
    return valid.includes(journey as Usertype) ? (journey as Usertype) : defaultJourneyFor(entityType);
}

type DatedRow = Record<string, unknown>;

// The blobs' transactions and invoices are seeded with fixed dates; move each set forward so its
// latest month is the current one, or the demo's ledger stops months before today.
function withCurrentDates(content: Record<string, unknown>): Record<string, unknown> {
    const bookkeeping = content.bookkeeping as { transactions?: DatedRow[] } | undefined;
    const invoicing = content.invoicing as { invoices?: DatedRow[] } | undefined;
    return {
        ...content,
        ...(bookkeeping && {
            bookkeeping: { ...bookkeeping, transactions: shiftRowsToNow(bookkeeping.transactions ?? [], ['date']) },
        }),
        ...(invoicing && {
            invoicing: { ...invoicing, invoices: shiftRowsToNow(invoicing.invoices ?? [], ['date', 'due']) },
        }),
    };
}

// Per-profession content blobs, keyed by entity_type. Each demo page narrows the
// loose Record<string, unknown> to its own ContentShape interface — keep the shared
// type loose so pages aren't forced into a single, sprawling union.
export const CONTENT: Record<EntityType, Record<string, unknown>> = {
    scorp: withCurrentDates(scorpContent as Record<string, unknown>),
    llc: withCurrentDates(llcContent as Record<string, unknown>),
};

// `entity_type` isn't part of the prod Member interface — the demo's MSW handler injects
// it (see demo/handlers/overrides.ts). Default to 'scorp' so the first render before
// useMember resolves shows something sensible instead of crashing.
export function getEntityType(member: unknown): EntityType {
    const raw = (member as { entity_type?: unknown } | undefined)?.entity_type;
    if (raw === 'scorp' || raw === 'llc') {
        return raw;
    }
    return 'scorp';
}
